import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import integrationConsentApi from '../services/integrationConsentApi';

export default function usePendingIntegrationConsents(options = {}) {
  const { company, isAuthenticated, user } = useAuth();
  const includeAll = Boolean(options.includeAll);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isCompanyAdmin = Boolean(isAuthenticated && company?.id && (user?.role === 'admin' || !user));

  const refresh = useCallback(async () => {
    if (!isCompanyAdmin) {
      setConsents([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await integrationConsentApi.listPending(includeAll ? { includeAll: 1 } : {});
      setConsents(data.consents || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load integration consent requests');
    } finally {
      setLoading(false);
    }
  }, [includeAll, isCompanyAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const approve = useCallback(async (id) => {
    const data = await integrationConsentApi.approve(id);
    if (includeAll) {
      await refresh();
    } else {
      setConsents((items) => items.filter((item) => item.id !== id));
    }
    return data;
  }, [includeAll, refresh]);

  const deny = useCallback(async (id, reason) => {
    const data = await integrationConsentApi.deny(id, reason);
    if (includeAll) {
      await refresh();
    } else {
      setConsents((items) => items.filter((item) => item.id !== id));
    }
    return data;
  }, [includeAll, refresh]);

  return {
    approve,
    consents,
    deny,
    error,
    loading,
    refresh
  };
}
